import { useShallow } from 'zustand/react/shallow';
import { useStore } from '~/store';
import type { RootState } from '~/store/slices';
import type { Rect } from '~/types/pipeline';

const EMPTY: number[] = [];

export function selectGridBounds(state: RootState): Rect | null {
  return state.grid.cropRect;
}

/**
 * Column dividers in absolute source-image coords. Returns the explicit
 * dividers if set; otherwise the implicit single-cell partition derived
 * from the crop bounds; or a stable EMPTY array when no crop exists.
 *
 * Returns a fresh array on each call when bounds-derived — pair with
 * `useShallow` (or `useColDividers`) at consumption sites to avoid
 * triggering unnecessary re-renders.
 */
export function selectColDividers(state: RootState): number[] {
  const bounds = state.grid.cropRect;
  const explicit = state.grid.colDividers;
  if (explicit) return explicit;
  if (!bounds) return EMPTY;
  return [bounds.x, bounds.x + bounds.w];
}

export function selectRowDividers(state: RootState): number[] {
  const bounds = state.grid.cropRect;
  const explicit = state.grid.rowDividers;
  if (explicit) return explicit;
  if (!bounds) return EMPTY;
  return [bounds.y, bounds.y + bounds.h];
}

export function useColDividers(): number[] {
  return useStore(useShallow(selectColDividers));
}

export function useRowDividers(): number[] {
  return useStore(useShallow(selectRowDividers));
}
